import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { pb } from '../lib/pocketbase.js';
import { useAuth } from '../auth/AuthContext.jsx';
import { formatKontaktName } from '../lib/kontakt.js';
import {
  ENCODING_OPTIONS,
  IMPORT_FIELDS,
  adressKey,
  autoMapColumns,
  buildAllVorschauen,
  decodeWithEncoding,
  detectEncodingAndDecode,
  emailKey,
  parseCSV,
} from '../lib/csvImport.js';

const QUELLE_OPTIONS = [
  { value: 'bemerkung', label: 'Aus Bemerkung' },
  { value: 'website', label: 'Aus Website' },
  { value: 'nachnamen', label: 'Aus Nachnamen' },
  { value: 'manuell', label: 'Manuell eingeben' },
];

const STEPS = [
  { num: 1, label: 'Datei' },
  { num: 2, label: 'Spalten' },
  { num: 3, label: 'Gruppierung' },
  { num: 4, label: 'Vorschau' },
  { num: 5, label: 'Import' },
];

const GRUPPIERUNG_OPTIONS = [
  {
    value: 'address',
    label: 'Gleiche Adresse (PLZ + Straße)',
    description: 'Alle Zeilen mit identischer PLZ und Straße werden zu einer Praxis zusammengefasst.',
  },
  {
    value: 'email_domain',
    label: 'Gleiche E-Mail-Domain',
    description: 'Alle Zeilen mit derselben Domain (z. B. @praxis-musterstadt.de) bilden eine Praxis.',
  },
  {
    value: 'row',
    label: 'Jede Zeile = eigene Praxis',
    description: 'Keine Gruppierung — jeder CSV-Eintrag wird als eigene Praxis angelegt.',
  },
];

function StepIndicator({ current }) {
  return (
    <div className="flex flex-wrap items-center gap-2">
      {STEPS.map((s, i) => {
        const active = s.num === current;
        const done = s.num < current;
        return (
          <div key={s.num} className="flex items-center gap-2">
            <span
              className={`flex h-7 w-7 items-center justify-center rounded-full text-xs font-semibold ${
                active
                  ? 'bg-primary-600 text-white'
                  : done
                  ? 'bg-primary-100 text-primary-700'
                  : 'bg-slate-200 text-slate-500'
              }`}
            >
              {s.num}
            </span>
            <span
              className={`text-sm ${
                active
                  ? 'font-semibold text-primary-700'
                  : done
                  ? 'text-primary-700'
                  : 'text-slate-400'
              }`}
            >
              {s.label}
            </span>
            {i < STEPS.length - 1 && <span className="text-slate-300">→</span>}
          </div>
        );
      })}
    </div>
  );
}

export default function AdminImportPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [step, setStep] = useState(1);

  // Step 1
  const [file, setFile] = useState(null);
  // Auto-Erkennung scort ab jetzt nach deutschen Sonderzeichen — erkennt
  // damit auch MacRoman und CP850 zuverlässig.
  const [encodingChoice, setEncodingChoice] = useState('auto');
  const [encoding, setEncoding] = useState(null); // tatsächlich verwendetes Encoding
  const [parsed, setParsed] = useState(null);
  const [parseError, setParseError] = useState(null);

  // Step 2
  const [mapping, setMapping] = useState({});

  // Step 3
  const [gruppierung, setGruppierung] = useState('address');

  // Step 4
  const [praxenVorschau, setPraxenVorschau] = useState([]);

  // Step 5
  const [importing, setImporting] = useState(false);
  const [progress, setProgress] = useState({ done: 0, total: 0, current: '' });
  const [stats, setStats] = useState(null);
  const [importError, setImportError] = useState(null);

  function resetAll() {
    setFile(null);
    setEncodingChoice('auto');
    setEncoding(null);
    setParsed(null);
    setParseError(null);
    setMapping({});
    setGruppierung('address');
    setPraxenVorschau([]);
    setImporting(false);
    setProgress({ done: 0, total: 0, current: '' });
    setStats(null);
    setImportError(null);
    setStep(1);
  }

  /**
   * Liest die Datei mit dem gewählten Encoding ein und parst sie.
   * Wird sowohl beim ersten Upload als auch beim manuellen Encoding-Wechsel
   * aufgerufen, damit Umlaute sich live korrigieren.
   */
  async function reparseFile(f, choice) {
    setParseError(null);
    try {
      let enc;
      let text;
      if (choice === 'auto') {
        const result = await detectEncodingAndDecode(f);
        enc = result.encoding;
        text = result.text;
      } else {
        enc = choice;
        text = await decodeWithEncoding(f, choice);
      }
      const result = parseCSV(text);
      if (result.headers.length === 0 || result.data.length === 0) {
        throw new Error('CSV enthält keine verwertbaren Daten.');
      }
      setEncoding(enc);
      setParsed(result);
      setMapping(autoMapColumns(result.headers));
    } catch (err) {
      setParseError(err?.message || 'CSV konnte nicht geparst werden.');
      setParsed(null);
      setEncoding(null);
    }
  }

  async function handleFileChange(e) {
    const f = e.target.files?.[0];
    if (!f) return;
    setFile(f);
    await reparseFile(f, encodingChoice);
  }

  async function handleEncodingChange(e) {
    const choice = e.target.value;
    setEncodingChoice(choice);
    if (file) {
      await reparseFile(file, choice);
    }
  }

  function handleMappingChange(field, column) {
    setMapping((prev) => ({ ...prev, [field]: column }));
  }

  function gotoStep(target) {
    if (importing) return;
    // Beim Wechsel auf Step 4 die Vorschau (neu) bauen.
    if (target === 4 && parsed) {
      setPraxenVorschau(buildAllVorschauen(parsed.data, mapping, gruppierung));
    }
    setStep(target);
  }

  function updatePraxisName(idx, name) {
    // Manuelles Tippen schaltet die Quelle automatisch auf "manuell" um.
    setPraxenVorschau((prev) => {
      const next = [...prev];
      next[idx] = { ...next[idx], name, quelle: 'manuell' };
      return next;
    });
  }

  function updatePraxisQuelle(idx, quelle) {
    setPraxenVorschau((prev) => {
      const next = [...prev];
      const p = next[idx];
      let neuerName = p.name;
      if (quelle !== 'manuell') {
        const kand = p.kandidaten?.[quelle];
        if (kand) neuerName = kand;
      }
      next[idx] = { ...p, quelle, name: neuerName };
      return next;
    });
  }

  async function runImport() {
    setImporting(true);
    setImportError(null);
    setStats(null);
    try {
      // Bestehende Praxen + Kontakte für Duplikat-Erkennung holen.
      const [bestehendePraxen, bestehendeKontakte] = await Promise.all([
        pb.collection('praxen').getFullList({ fields: 'id,plz,strasse' }),
        pb.collection('kontakte').getFullList({ fields: 'id,email' }),
      ]);

      const adressSet = new Set(
        bestehendePraxen.map((p) => adressKey(p.plz, p.strasse)).filter(Boolean)
      );
      const emailSet = new Set(
        bestehendeKontakte.map((k) => emailKey(k.email)).filter(Boolean)
      );

      const result = {
        praxen: 0,
        kontakte: 0,
        duplikate_praxen: 0,
        duplikate_kontakte: 0,
      };

      setProgress({ done: 0, total: praxenVorschau.length, current: '' });

      for (let i = 0; i < praxenVorschau.length; i++) {
        const v = praxenVorschau[i];
        setProgress({ done: i, total: praxenVorschau.length, current: v.name });

        const key = adressKey(v.plz, v.strasse);
        if (key && adressSet.has(key)) {
          result.duplikate_praxen += 1;
          continue;
        }

        let createdPraxis;
        try {
          createdPraxis = await pb.collection('praxen').create({
            name: v.name || 'Unbenannte Praxis',
            typ: 'praxis',
            status: 'interessent',
            strasse: v.strasse,
            plz: v.plz,
            ort: v.ort,
            telefon: v.telefon,
            email: v.email,
            website: v.website,
            fachgebiet: v.fachgebiet,
            // fachgebiet_neu ist bereits durch normalisiereFachrichtungImport
            // gegen den Slug-Katalog validiert — leerer String, wenn die
            // CSV-Spalte keinen exakten Treffer hatte.
            fachgebiet_neu: v.fachgebiet_neu || '',
            notizen: v.bemerkung,
            angelegt_von: user?.id ?? null,
          });
          result.praxen += 1;
          if (key) adressSet.add(key);
        } catch (err) {
          console.warn('Praxis-Anlage fehlgeschlagen', v, err);
          continue;
        }

        for (const k of v.kontakte) {
          if (!k.vorname && !k.nachname) continue;
          const ek = emailKey(k.email);
          if (ek && emailSet.has(ek)) {
            result.duplikate_kontakte += 1;
            continue;
          }
          try {
            await pb.collection('kontakte').create({
              praxis: createdPraxis.id,
              titel: k.titel,
              vorname: k.vorname,
              nachname: k.nachname,
              email: k.email,
              telefon: k.telefon,
              rolle: 'arzt',
              angelegt_von: user?.id ?? null,
            });
            result.kontakte += 1;
            if (ek) emailSet.add(ek);
          } catch (err) {
            console.warn('Kontakt-Anlage fehlgeschlagen', k, err);
          }
        }
      }

      setProgress({ done: praxenVorschau.length, total: praxenVorschau.length, current: '' });
      setStats(result);
    } catch (err) {
      setImportError(err?.message || 'Import fehlgeschlagen.');
    } finally {
      setImporting(false);
    }
  }

  const previewRows = useMemo(() => parsed?.data.slice(0, 5) ?? [], [parsed]);
  const headers = parsed?.headers ?? [];

  // Mapping-Validierung: Vor- und Nachname müssen mindestens gemappt sein.
  const mappingValid = !!(mapping.vorname || mapping.nachname);

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-slate-900">CSV-Import</h1>
        <p className="mt-1 text-sm text-slate-600">
          Importiere Kontakte aus einer CSV-Datei und lass das System automatisch
          Praxen anlegen und Duplikate erkennen.
        </p>
      </div>

      <div className="card space-y-6">
        <StepIndicator current={step} />

        {/* ---------- Step 1: Datei ---------- */}
        {step === 1 && (
          <div className="space-y-4">
            <h2 className="text-lg font-semibold text-slate-900">1. CSV-Datei auswählen</h2>
            <p className="text-sm text-slate-600">
              Trennzeichen (Semikolon oder Komma) wird automatisch erkannt.
              Beim Encoding kannst du erzwingen, was du brauchst — wenn du
              kaputte Umlaute siehst, wechsle hier auf ISO-8859-1 oder
              Windows-1252.
            </p>

            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <div>
                <label className="label">CSV-Datei</label>
                <input
                  type="file"
                  accept=".csv,text/csv"
                  onChange={handleFileChange}
                  className="mt-1 block w-full text-sm text-slate-700 file:mr-3 file:rounded-md file:border-0 file:bg-primary-50 file:px-4 file:py-2 file:text-sm file:font-medium file:text-primary-700 hover:file:bg-primary-100"
                />
              </div>

              <div>
                <label className="label">Encoding</label>
                <select
                  className="input mt-1"
                  value={encodingChoice}
                  onChange={handleEncodingChange}
                >
                  {ENCODING_OPTIONS.map((opt) => (
                    <option key={opt.value} value={opt.value}>
                      {opt.label}
                    </option>
                  ))}
                </select>
                <p className="mt-1 text-xs text-slate-500">
                  Wechsel parsst die Datei sofort neu — die Vorschau zeigt
                  direkt, ob Umlaute korrekt sind.
                </p>
              </div>
            </div>

            {parseError && (
              <div className="rounded-md bg-red-50 p-3 text-sm text-red-700 ring-1 ring-red-200">
                {parseError}
              </div>
            )}

            {parsed && (
              <>
                <div className="rounded-md bg-emerald-50 p-3 text-sm text-emerald-800 ring-1 ring-emerald-200">
                  ✓ {file?.name} eingelesen — {parsed.data.length} Zeilen,{' '}
                  {parsed.headers.length} Spalten · Trennzeichen{' '}
                  <span className="font-mono">
                    {parsed.delimiter === ';' ? '";"' : '","'}
                  </span>{' '}
                  · Encoding <span className="font-mono">{encoding}</span>
                  {encodingChoice !== 'auto' && (
                    <span className="ml-1 text-emerald-700">(manuell gewählt)</span>
                  )}
                </div>

                <div>
                  <h3 className="mb-2 text-sm font-semibold text-slate-700">
                    Header-Vorschau (zur Encoding-Kontrolle)
                  </h3>
                  <div className="overflow-x-auto rounded-md bg-slate-50 p-3 text-xs ring-1 ring-slate-200">
                    <code className="whitespace-pre text-slate-700">
                      {parsed.headers.join(' · ')}
                    </code>
                  </div>
                  <p className="mt-1 text-xs text-slate-500">
                    Wenn hier kaputte Zeichen wie &bdquo;Ÿ&ldquo; oder &bdquo;§&ldquo; statt
                    Umlaute auftauchen, oben das Encoding wechseln.
                  </p>
                </div>
              </>
            )}

            <div className="flex justify-end gap-2 pt-2">
              <button
                type="button"
                className="btn-primary"
                disabled={!parsed}
                onClick={() => gotoStep(2)}
              >
                Weiter zum Mapping →
              </button>
            </div>
          </div>
        )}

        {/* ---------- Step 2: Spalten ---------- */}
        {step === 2 && parsed && (
          <div className="space-y-4">
            <h2 className="text-lg font-semibold text-slate-900">2. Spalten zuordnen</h2>
            <p className="text-sm text-slate-600">
              Wähle für jedes CRM-Feld die passende CSV-Spalte. Vorschlag wurde
              automatisch aus den Spaltennamen erzeugt.
            </p>

            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              {IMPORT_FIELDS.map((field) => (
                <div key={field.key}>
                  <label className="label">{field.label}</label>
                  <select
                    className="input mt-1"
                    value={mapping[field.key] || ''}
                    onChange={(e) => handleMappingChange(field.key, e.target.value)}
                  >
                    <option value="">— nicht zuordnen —</option>
                    {headers.map((h) => (
                      <option key={h} value={h}>
                        {h}
                      </option>
                    ))}
                  </select>
                </div>
              ))}
            </div>

            {!mappingValid && (
              <div className="rounded-md bg-amber-50 p-3 text-sm text-amber-800 ring-1 ring-amber-200">
                Mindestens Vorname oder Nachname muss zugeordnet sein, damit Kontakte
                angelegt werden können.
              </div>
            )}

            <div>
              <h3 className="mb-2 mt-4 text-sm font-semibold text-slate-700">
                Vorschau (erste 5 Zeilen)
              </h3>
              <div className="overflow-x-auto rounded-md ring-1 ring-slate-200">
                <table className="min-w-full divide-y divide-slate-200 text-xs">
                  <thead className="bg-slate-50">
                    <tr>
                      {headers.map((h) => (
                        <th
                          key={h}
                          className="px-3 py-2 text-left font-semibold text-slate-600"
                        >
                          {h}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100 bg-white">
                    {previewRows.map((row, idx) => (
                      <tr key={idx}>
                        {headers.map((h) => (
                          <td key={h} className="px-3 py-2 text-slate-700">
                            {row[h] || '—'}
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            <div className="flex justify-between pt-2">
              <button type="button" className="btn-secondary" onClick={() => gotoStep(1)}>
                ← Zurück
              </button>
              <button
                type="button"
                className="btn-primary"
                disabled={!mappingValid}
                onClick={() => gotoStep(3)}
              >
                Weiter zur Gruppierung →
              </button>
            </div>
          </div>
        )}

        {/* ---------- Step 3: Gruppierung ---------- */}
        {step === 3 && (
          <div className="space-y-4">
            <h2 className="text-lg font-semibold text-slate-900">3. Praxen erkennen</h2>
            <p className="text-sm text-slate-600">
              Wie sollen Zeilen zu einer Praxis zusammengefasst werden?
            </p>

            <div className="space-y-2">
              {GRUPPIERUNG_OPTIONS.map((opt) => (
                <label
                  key={opt.value}
                  className={`flex cursor-pointer items-start gap-3 rounded-md border p-3 transition ${
                    gruppierung === opt.value
                      ? 'border-primary-500 bg-primary-50'
                      : 'border-slate-200 hover:bg-slate-50'
                  }`}
                >
                  <input
                    type="radio"
                    name="gruppierung"
                    value={opt.value}
                    checked={gruppierung === opt.value}
                    onChange={() => setGruppierung(opt.value)}
                    className="mt-1 h-4 w-4 border-slate-300 text-primary-600 focus:ring-primary-500"
                  />
                  <div>
                    <div className="text-sm font-medium text-slate-900">{opt.label}</div>
                    <div className="text-xs text-slate-500">{opt.description}</div>
                  </div>
                </label>
              ))}
            </div>

            <div className="flex justify-between pt-2">
              <button type="button" className="btn-secondary" onClick={() => gotoStep(2)}>
                ← Zurück
              </button>
              <button type="button" className="btn-primary" onClick={() => gotoStep(4)}>
                Vorschau anzeigen →
              </button>
            </div>
          </div>
        )}

        {/* ---------- Step 4: Vorschau ---------- */}
        {step === 4 && (
          <div className="space-y-4">
            <h2 className="text-lg font-semibold text-slate-900">
              4. Praxen-Vorschau ({praxenVorschau.length})
            </h2>
            <p className="text-sm text-slate-600">
              Praxisnamen wurden automatisch ermittelt (Bemerkung → Website-Domain →
              Arztname). Du kannst sie hier vor dem Import noch anpassen. Der
              Inhalt der „Zusatzinfo"-Spalte landet automatisch in den Notizen
              der Praxis.
            </p>

            <div className="overflow-x-auto rounded-md ring-1 ring-slate-200">
              <table className="min-w-full divide-y divide-slate-200">
                <thead className="bg-slate-50">
                  <tr>
                    <th className="px-3 py-2 text-left text-xs font-semibold uppercase tracking-wider text-slate-500">
                      Praxisname
                    </th>
                    <th className="px-3 py-2 text-left text-xs font-semibold uppercase tracking-wider text-slate-500">
                      Kontakte
                    </th>
                    <th className="px-3 py-2 text-left text-xs font-semibold uppercase tracking-wider text-slate-500">
                      Adresse
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100 bg-white">
                  {praxenVorschau.length === 0 && (
                    <tr>
                      <td colSpan={3} className="px-3 py-6 text-center text-sm text-slate-500">
                        Keine Praxen ermittelt — bitte Mapping oder Gruppierung prüfen.
                      </td>
                    </tr>
                  )}
                  {praxenVorschau.map((p, idx) => (
                    <tr key={idx} className="align-top">
                      <td className="px-3 py-2">
                        <div className="flex flex-col gap-1 sm:flex-row sm:items-start">
                          <input
                            type="text"
                            className="input text-sm sm:flex-1"
                            value={p.name}
                            onChange={(e) => updatePraxisName(idx, e.target.value)}
                          />
                          <select
                            className="input text-xs sm:w-48"
                            value={p.quelle}
                            onChange={(e) => updatePraxisQuelle(idx, e.target.value)}
                            title="Quelle des Praxisnamens wechseln"
                          >
                            {QUELLE_OPTIONS.map((opt) => {
                              const verfuegbar =
                                opt.value === 'manuell' || !!p.kandidaten?.[opt.value];
                              return (
                                <option
                                  key={opt.value}
                                  value={opt.value}
                                  disabled={!verfuegbar}
                                >
                                  {opt.label}
                                  {!verfuegbar ? ' (leer)' : ''}
                                </option>
                              );
                            })}
                          </select>
                        </div>
                        {p.bemerkung && (
                          <div className="mt-1 line-clamp-2 text-xs italic text-slate-500">
                            Bemerkung: {p.bemerkung}
                          </div>
                        )}
                      </td>
                      <td className="px-3 py-2 text-sm text-slate-700">
                        <div className="font-medium">
                          {p.kontakte.length} Person{p.kontakte.length === 1 ? '' : 'en'}
                        </div>
                        <ul className="mt-1 space-y-0.5 text-xs text-slate-500">
                          {p.kontakte.slice(0, 3).map((k, ki) => (
                            <li key={ki}>
                              {formatKontaktName(k) || '—'}
                            </li>
                          ))}
                          {p.kontakte.length > 3 && (
                            <li>… und {p.kontakte.length - 3} weitere</li>
                          )}
                        </ul>
                      </td>
                      <td className="px-3 py-2 text-sm text-slate-700">
                        {p.strasse && <div>{p.strasse}</div>}
                        {(p.plz || p.ort) && (
                          <div>{[p.plz, p.ort].filter(Boolean).join(' ')}</div>
                        )}
                        {!p.strasse && !p.plz && !p.ort && '—'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div className="flex justify-between pt-2">
              <button type="button" className="btn-secondary" onClick={() => gotoStep(3)}>
                ← Zurück
              </button>
              <button
                type="button"
                className="btn-primary"
                disabled={praxenVorschau.length === 0}
                onClick={() => {
                  setStep(5);
                  // Import direkt nach dem Step-Wechsel anstoßen.
                  setTimeout(() => runImport(), 0);
                }}
              >
                Import starten →
              </button>
            </div>
          </div>
        )}

        {/* ---------- Step 5: Import ---------- */}
        {step === 5 && (
          <div className="space-y-4">
            <h2 className="text-lg font-semibold text-slate-900">5. Import läuft</h2>

            <div className="space-y-2">
              <div className="h-3 w-full overflow-hidden rounded-full bg-slate-200">
                <div
                  className="h-full bg-primary-600 transition-all duration-150"
                  style={{
                    width:
                      progress.total > 0
                        ? `${(progress.done / progress.total) * 100}%`
                        : '0%',
                  }}
                />
              </div>
              <p className="text-sm text-slate-600">
                {progress.done} / {progress.total}
                {progress.current && ` — aktuell: ${progress.current}`}
              </p>
            </div>

            {importError && (
              <div className="rounded-md bg-red-50 p-3 text-sm text-red-700 ring-1 ring-red-200">
                {importError}
              </div>
            )}

            {stats && !importing && (
              <div className="space-y-3 rounded-lg border border-emerald-200 bg-emerald-50 p-5">
                <h3 className="text-base font-semibold text-emerald-900">
                  ✓ Import abgeschlossen
                </h3>
                <ul className="space-y-1 text-sm text-emerald-900">
                  <li>
                    <strong>{stats.praxen}</strong> Praxen angelegt
                  </li>
                  <li>
                    <strong>{stats.kontakte}</strong> Kontakte angelegt
                  </li>
                  <li>
                    <strong>{stats.duplikate_praxen}</strong> Praxen-Duplikate
                    übersprungen (gleiche PLZ + Straße)
                  </li>
                  <li>
                    <strong>{stats.duplikate_kontakte}</strong> Kontakt-Duplikate
                    übersprungen (gleiche E-Mail)
                  </li>
                </ul>
                <div className="flex flex-wrap gap-2 pt-2">
                  <button
                    type="button"
                    className="btn-primary"
                    onClick={() => navigate('/praxen')}
                  >
                    Zur Praxen-Liste
                  </button>
                  <button type="button" className="btn-secondary" onClick={resetAll}>
                    Weiteren Import starten
                  </button>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
